import React from "react";
import { Area, CartesianGrid, ComposedChart, Line, ReferenceArea, XAxis, YAxis } from "recharts";
import { endOfDay, format, startOfDay } from "date-fns";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { ChartContainer, ChartTooltip } from "../ui/chart";
import { buildSeries, formatDuration, timeTicks, toSamples } from "src/lib/historyChart";
import {
  ChartZoomControls,
  defaultZoomPreset,
  useDragZoom,
  zoomPresets,
  zoomWindow,
} from "./ChartZoom";

const MIN_ZOOM_MS = 30 * 60 * 1000; // Smaller drags are treated as clicks

/*
 * Shared history chart of the Victron dashboard (battery, temperature, consumption, fan, camera).
 * Draws every sample for short periods; for long periods the samples are grouped in time buckets
 * (see src/lib/historyChart.js) and the card says so.
 * Long periods open zoomed to the last days (see ChartZoom.js); the buckets are computed for the
 * visible range only, so zooming in shows more detail.
 *
 * props:
 *   title, subject       card title and the text after "Showing" ("battery voltage")
 *   history              {"<epoch_ms>": "<value>"} map from the backend
 *   parse                value -> number | null (null = skip the sample)
 *   kind                 "numeric" | "state" (on/off)
 *   unit, yLabel         y axis unit ("V") and label ("Voltage (V)")
 *   color                line color
 *   startDate, endDate   selected period (x axis range)
 */
export default function HistoricLineChart({
  title,
  subject,
  history,
  parse,
  kind = "numeric",
  unit = "",
  yLabel,
  color = "hsl(var(--chart-1))",
  startDate,
  endDate,
}) {
  const isState = kind === "state";

  const samples = React.useMemo(() => toSamples(history, parse), [history, parse]);

  // Full x range: the selected days, widened if a sample falls outside them
  const [fullFrom, fullTo] = React.useMemo(() => {
    const first = samples[0]?.ts;
    const last = samples[samples.length - 1]?.ts;
    const start = startDate ? startOfDay(new Date(startDate)).getTime() : first;
    const end = endDate ? endOfDay(new Date(endDate)).getTime() : last;
    return [Math.min(start ?? first, first ?? start), Math.max(end ?? last, last ?? end)];
  }, [samples, startDate, endDate]);

  // Zoom: { preset: "24h" | "7d" | "30d" | "all" } or { preset: "custom", from, to }
  const span = Number.isFinite(fullTo - fullFrom) ? fullTo - fullFrom : 0;
  const presets = React.useMemo(() => zoomPresets(span), [span]);
  const [zoom, setZoom] = React.useState(() => ({ preset: defaultZoomPreset(span) }));
  React.useEffect(() => {
    setZoom({ preset: defaultZoomPreset(span) }); // New period: back to the default zoom
  }, [span]);

  const [from, to] = zoomWindow(zoom, fullFrom, fullTo);
  const zoomed = zoom.preset !== "all";

  const series = React.useMemo(
    () => buildSeries(samples.filter((s) => s.ts >= from && s.ts <= to), { kind }),
    [samples, from, to, kind]
  );
  const { points, reduced, bucketMs, sampleCount } = series;

  const { drag, handlers: dragHandlers } = useDragZoom({
    getValue: (event) => (typeof event?.activeLabel === "number" ? event.activeLabel : null),
    onZoom: (start, end) => setZoom({ preset: "custom", from: start, to: end }),
    minSpan: MIN_ZOOM_MS,
  });

  const { ticks, formatTick } = React.useMemo(
    () => (Number.isFinite(from) && Number.isFinite(to) ? timeTicks(from, to) : { ticks: [], formatTick: () => "" }),
    [from, to]
  );

  const periodText = startDate
    ? sameDay(startDate, endDate)
      ? `Showing ${subject} on ${format(new Date(startDate), "dd/MM/yyyy")}`
      : `Showing ${subject} from ${format(new Date(startDate), "dd/MM/yyyy")} to ${format(new Date(endDate ?? startDate), "dd/MM/yyyy")}`
    : `Showing ${subject}`;

  const zoomText = zoomed && Number.isFinite(from) && Number.isFinite(to)
    ? `Zoomed to ${format(new Date(from), "dd/MM/yyyy HH:mm")} – ${format(new Date(to), "dd/MM/yyyy HH:mm")}`
    : null;

  const detailText = sampleCount === 0
    ? null
    : reduced
      ? `${sampleCount.toLocaleString()} readings, reduced to one point per ${formatDuration(bucketMs)}: ` +
        (isState ? "share of time ON in each period." : "average, with the min–max range shaded.")
      : `${sampleCount.toLocaleString()} readings.`;

  // Points between two gaps have no line segment: draw them as dots so they stay visible
  const renderDot = ({ cx, cy, index }) => {
    const prev = points[index - 1];
    const next = points[index + 1];
    const isolated = points[index]?.value !== null && (!prev || prev.value === null) && (!next || next.value === null);
    return isolated && cx !== undefined && cy !== undefined
      ? <circle key={index} cx={cx} cy={cy} r={2.5} fill={color} />
      : <g key={index} />;
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return "-";
    if (isState) return reduced ? `${Math.round(value * 100)}%` : value === 1 ? "on" : "off";
    return `${value.toLocaleString()} ${unit}`.trim();
  };

  const renderTooltip = ({ active, payload }) => {
    const point = payload?.[0]?.payload;
    if (!active || !point || point.value === null) return null;

    const when = reduced
      ? `${format(new Date(point.ts - bucketMs / 2), "dd/MM/yyyy HH:mm")} – ${format(new Date(point.ts + bucketMs / 2), "HH:mm")}`
      : format(new Date(point.ts), "dd/MM/yyyy HH:mm");

    return (
      <div className="rounded-lg border bg-background px-3 py-2 text-xs shadow-xl">
        <div className="font-medium">{when}</div>
        {isState ? (
          <div>{reduced ? `ON ${formatValue(point.value)} of the time` : `State: ${formatValue(point.value)}`}</div>
        ) : (
          <>
            <div>{reduced ? "Average" : "Value"}: {formatValue(point.value)}</div>
            {reduced && point.range[0] !== point.range[1] && (
              <div className="text-muted-foreground">
                Min {formatValue(point.range[0])} · Max {formatValue(point.range[1])}
              </div>
            )}
          </>
        )}
        {reduced && <div className="text-muted-foreground">{point.count} readings</div>}
      </div>
    );
  };

  const chartConfig = { value: { label: subject, color } };

  return (
    <Card>
      <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row">
        <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-5 sm:py-6">
          <CardTitle>{title}</CardTitle>
          <CardDescription>{periodText}</CardDescription>
          {zoomText && <CardDescription className="text-xs font-medium">{zoomText}</CardDescription>}
          {detailText && <CardDescription className="text-xs">{detailText}</CardDescription>}
        </div>
      </CardHeader>
      <CardContent>
        {samples.length > 0 && (
          <ChartZoomControls
            className="mt-4"
            presets={presets}
            value={zoom.preset}
            onChange={(preset) => setZoom({ preset })}
          />
        )}
        {sampleCount === 0 ? (
          <div className="flex h-[250px] items-center justify-center text-sm text-muted-foreground">
            {samples.length === 0 ? "No data for the selected period" : "No data in this date range"}
          </div>
        ) : (
          <ChartContainer config={chartConfig} data-swapy-no-drag className="chart-zoomable aspect-auto h-[250px] w-full mt-2">
            <ComposedChart data={points} margin={{ left: 12, right: 12, bottom: 25 }} {...dragHandlers}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="ts"
                type="number"
                scale="time"
                domain={[from, to]}
                ticks={ticks}
                tickFormatter={formatTick}
                interval="preserveStartEnd"
                minTickGap={24}
                tickLine={false}
                axisLine={false}
                tickMargin={10}
                label={{
                  value: "Date",
                  position: "insideBottom",
                  offset: -20,
                  style: { textAnchor: "middle", fill: "#888" },
                }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                width={isState ? 56 : 64}
                domain={isState ? [0, 1] : ["auto", "auto"]}
                ticks={isState ? (reduced ? [0, 0.5, 1] : [0, 1]) : undefined}
                tickFormatter={(v) => (isState ? formatValue(v) : `${Number(v.toFixed(2))} ${unit}`.trim())}
                label={{
                  value: yLabel,
                  angle: -90,
                  position: "insideLeft",
                  style: { textAnchor: "middle", fill: "#888" },
                }}
              />
              <ChartTooltip cursor={false} content={renderTooltip} />
              {reduced && !isState && (
                <Area
                  dataKey="range"
                  type="monotone"
                  stroke="none"
                  fill={color}
                  fillOpacity={0.18}
                  connectNulls={false}
                  isAnimationActive={false}
                  activeDot={false}
                />
              )}
              <Line
                dataKey="value"
                type={isState && !reduced ? "stepAfter" : "monotone"}
                stroke={color}
                strokeWidth={2}
                dot={renderDot}
                activeDot={{ r: 3 }}
                connectNulls={false}
                isAnimationActive={false}
              />
              {drag && (
                <ReferenceArea x1={drag.start} x2={drag.end} fill={color} fillOpacity={0.12} strokeOpacity={0} />
              )}
            </ComposedChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}

const sameDay = (a, b) =>
  !b || startOfDay(new Date(a)).getTime() === startOfDay(new Date(b)).getTime();
